import { useMemo } from 'react'
import { fbt } from 'fbtee'
import { Users } from 'lucide-react'
import { useTotemFollowers } from '../../hooks/useTotemFollows'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { EmptyState } from '../EmptyState'
import { UserRow } from '../UserRow'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import type { Totem } from '../../types'

// Anonymous follows are free to create (a QR scan needs no account), so this is
// a rough signal of reach rather than a real audience metric — which is why it
// only ever shows to the group, never on a public surface.
export function FollowerCount({
  groupId,
  totemId,
  onClick,
}: {
  groupId: string
  totemId: string
  onClick: () => void
}) {
  const { followers } = useTotemFollowers(groupId, totemId)
  if (followers.length === 0) return null

  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation()
        onClick()
      }}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      aria-label={fbt('View followers', 'Accessible label for the totem follower count button')}
    >
      <Users className="size-3.5" />
      {followers.length}
    </button>
  )
}

export function TotemFollowersDialog({
  open,
  onOpenChange,
  groupId,
  totem,
  canRemove,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  totem: Totem
  canRemove: boolean
}) {
  const { followers, removeFollower } = useTotemFollowers(groupId, totem.id)
  const profiles = useUserProfiles(useMemo(() => followers.map(f => f.userId), [followers]))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Title of the dialog listing who follows a totem">
              Following <fbt:param name="totemName">{totem.name}</fbt:param>
            </fbt>
          </DialogTitle>
        </DialogHeader>
        {followers.length === 0 ? (
          <EmptyState>
            {fbt(
              'Nobody is following yet. Share the totem’s code to let people follow along.',
              'Empty state in the totem followers dialog',
            )}
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-1 max-h-80 overflow-y-auto">
            {followers.map(follow => (
              <li key={follow.id}>
                <UserRow
                  profile={profiles[follow.userId]}
                  // A follower who scanned the code and never signed up has no
                  // profile doc — they're still a real person standing there.
                  name={
                    profiles[follow.userId]?.displayName ??
                    fbt('Guest', 'Fallback name for a totem follower with no account')
                  }
                  action={
                    canRemove ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFollower(follow)}
                      >
                        {fbt('Remove', 'Button removing a follower from a totem')}
                      </Button>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
